import { apiClient } from './client';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE } from '@aegislab/shared';
import type { SignedDownloadUrlResponse, SignedUploadUrlResponse } from '@aegislab/shared';

// ─── Constants ──────────────────────────────────────────
// 허용 목록·크기 상한은 shared 가 단일 소스 — 사본을 두면 서버와 갈라져
// 프론트는 통과시키고 서버가 거부하는 .md 류 버그가 재발한다.

export { ALLOWED_MIME_TYPES, MAX_FILE_SIZE };

const EXTENSION_TO_MIME: Record<string, (typeof ALLOWED_MIME_TYPES)[number]> = {
  '.md': 'text/markdown',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
};

const MAX_RETRIES = 2;
const UPLOAD_TIMEOUT_MS = 30_000;

// ─── AbortSignal compose ───────────────────────────────
// AbortSignal.any()는 Node 20.3+ / Chrome 116+ / Safari 17.4+ 요구.
// 구형 환경 호환을 위해 수동 compose 헬퍼를 쓴다 — 한 signal이라도 abort되면
// 합성 signal을 abort한다.
function composeAbortSignals(signals: Array<AbortSignal | undefined>): AbortSignal | undefined {
  const valid = signals.filter((s): s is AbortSignal => !!s);
  if (valid.length === 0) return undefined;
  if (valid.length === 1) return valid[0];

  const controller = new AbortController();
  const onAbort = (reason?: unknown) => {
    controller.abort(reason);
    for (const s of valid) s.removeEventListener('abort', handler);
  };
  const handler = () => onAbort();
  for (const s of valid) {
    if (s.aborted) {
      controller.abort((s as { reason?: unknown }).reason);
      return controller.signal;
    }
    s.addEventListener('abort', handler);
  }
  return controller.signal;
}

// ─── UploadError ────────────────────────────────────────

export type UploadErrorCode =
  'FILE_TOO_LARGE' | 'INVALID_MIME_TYPE' | 'UPLOAD_FAILED' | 'UPLOAD_TIMEOUT';

export class UploadError extends Error {
  constructor(
    public readonly code: UploadErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

// ─── Validation ─────────────────────────────────────────

export function validateFile(file: File): string {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    throw new UploadError(
      'FILE_TOO_LARGE',
      `파일 크기가 너무 큽니다 (${sizeMB}MB). 최대 10MB까지 업로드할 수 있습니다.`
    );
  }
  let mime = file.type;
  if (!mime) {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    mime = EXTENSION_TO_MIME[ext] ?? '';
  }
  if (!ALLOWED_MIME_TYPES.includes(mime as (typeof ALLOWED_MIME_TYPES)[number])) {
    throw new UploadError(
      'INVALID_MIME_TYPE',
      `허용되지 않는 파일 형식입니다 (${file.type || '알 수 없음'}). 이미지, PDF, 문서 파일만 업로드할 수 있습니다.`
    );
  }
  return mime;
}

// ─── API ────────────────────────────────────────────────

/**
 * Get a signed upload URL from the backend.
 * userId is extracted from JWT on the server side.
 */
export async function getSignedUploadUrl(
  filename: string,
  mimeType: string,
  bucket?: string,
  signal?: AbortSignal
): Promise<SignedUploadUrlResponse> {
  const response = await apiClient.post<SignedUploadUrlResponse>(
    '/upload/signed-url',
    { filename, mimeType, bucket },
    { signal }
  );
  return response.data;
}

/**
 * Upload a file directly to Supabase Storage using the signed URL.
 * Includes response validation, timeout (30s), and retry with exponential backoff.
 * 외부 signal(예: unmount)을 받으면 즉시 중단 — timeout controller와 compose.
 */
export async function uploadFileToStorage(
  signedUrl: string,
  file: File,
  mimeType?: string,
  externalSignal?: AbortSignal
): Promise<void> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // 외부 signal이 이미 abort 상태면 즉시 전파 (불필요한 재시도 방지).
    if (externalSignal?.aborted) {
      throw new DOMException('Aborted by external signal', 'AbortError');
    }

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), UPLOAD_TIMEOUT_MS);
    // 외부 signal abort 시 timeout controller도 abort (catch에서AbortError 인식 위해).
    const externalAbortHandler = () =>
      timeoutController.abort((externalSignal as { reason?: unknown })?.reason);
    if (externalSignal) {
      if (externalSignal.aborted) externalAbortHandler();
      else externalSignal.addEventListener('abort', externalAbortHandler, { once: true });
    }
    const composedSignal = composeAbortSignals([timeoutController.signal, externalSignal]);

    try {
      const response = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType || file.type },
        body: file,
        signal: composedSignal,
      });

      if (!response.ok) {
        throw new UploadError(
          'UPLOAD_FAILED',
          `업로드 실패 (HTTP ${response.status}). 잠시 후 다시 시도해주세요.`
        );
      }
      return; // success
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // 외부 signal에 의한 abort면 재시도 없이 즉시 throw (사용자 의도적 취소).
        if (externalSignal?.aborted) {
          throw error;
        }
        lastError = new UploadError(
          'UPLOAD_TIMEOUT',
          '업로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.'
        );
      } else if (error instanceof UploadError) {
        lastError = error;
      } else {
        lastError = new UploadError(
          'UPLOAD_FAILED',
          `업로드 중 오류가 발생했습니다: ${(error as Error).message}`
        );
      }

      if (attempt < MAX_RETRIES) {
        const delay = 1000 * 2 ** attempt; // 1s, 2s
        await new Promise((r) => setTimeout(r, delay));
      }
    } finally {
      clearTimeout(timeoutId);
      if (externalSignal) {
        externalSignal.removeEventListener('abort', externalAbortHandler);
      }
    }
  }

  if (lastError) throw lastError;
  throw new Error('Upload failed');
}

/**
 * Delete an uploaded file from storage (best-effort).
 */
export async function deleteUploadedFile(path: string, bucket?: string): Promise<void> {
  await apiClient.delete(`/upload/${encodeURIComponent(path)}`, {
    params: { bucket },
  });
}

/**
 * Get a signed download URL for a stored file
 */
/** filename 을 주면 그 이름으로 내려받는 주소를 받는다 — 저장 객체 이름은 UUID 다. */
export async function getSignedDownloadUrl(
  path: string,
  bucket?: string,
  filename?: string
): Promise<string> {
  const response = await apiClient.get<SignedDownloadUrlResponse>('/upload/signed-url', {
    params: filename ? { path, bucket, filename } : { path, bucket },
  });
  return response.data.signedUrl;
}
